/**
 * InviteIntroAnimation — Video-based intro for /invite/:token
 *
 * Plays the M-Vitations unlock animation (new intro video).
 * At end: smooth 1.0s transparency fade → glass invitation card revealed.
 * Background matches InviteLandingPage exactly (same image URL + attachment)
 * so there is zero visible background shift during the transition.
 *
 * Alignment strategy:
 *   height: 100vh; width: auto centers the landscape video so its
 *   horizontal midpoint maps to the viewport midpoint — the same axis
 *   the DOM card is centered on.  The final-frame card therefore
 *   appears at the same X position as the live invitation card.
 *
 * Tap anywhere to skip.
 */
import React, { useState, useRef, useCallback } from 'react';

// Portrait-native intro video — corrected (no baked-in guest name)
const VIDEO_URL =
  'https://customer-assets.emergentagent.com/job_49133e57-f6cd-4b7b-9084-054c1edd8062/artifacts/mucuk6fw_4.13.26.Intro%20portrait%20final-5.mp4';

// Must be identical to InviteLandingPage background for continuity
const BG_IMAGE_URL =
  'https://customer-assets.emergentagent.com/job_b4805a07-765d-4b82-bd36-89754495be40/artifacts/wmrlspr0_new%20background%20for%20site%202.png';

export default function InviteIntroAnimation({ onComplete }) {
  const videoRef       = useRef(null);
  const calledRef      = useRef(false);  // guard against double-firing
  const [fading, setFading] = useState(false);

  const triggerEnd = useCallback(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (videoRef.current) videoRef.current.pause();
    setFading(true);
    // Notify parent after 1.0s fade completes
    setTimeout(() => onComplete?.(), 1000);
  }, [onComplete]);

  return (
    <div
      onClick={triggerEnd}
      style={{
        position:            'fixed',
        inset:               0,
        zIndex:              100,
        // ── Background continuity ─────────────────────────────────────────
        // Same image + attachment as InviteLandingPage so letterbox areas
        // and the transparency fade are visually identical to the page.
        backgroundImage:     `url(${BG_IMAGE_URL})`,
        backgroundSize:      'cover',
        backgroundPosition:  'center center',
        backgroundRepeat:    'no-repeat',
        backgroundAttachment:'fixed',
        // ── Layout ───────────────────────────────────────────────────────
        display:             'flex',
        alignItems:          'center',
        justifyContent:      'center',
        overflow:            'hidden',
        cursor:              'pointer',
        // ── Fade ─────────────────────────────────────────────────────────
        opacity:             fading ? 0 : 1,
        transition:          fading ? 'opacity 1.0s ease' : 'none',
        pointerEvents:       fading ? 'none' : 'auto',
      }}
    >
      <style>{`
        @keyframes mv-tap-hint {
          0%,100% { opacity: 0.20; }
          50%     { opacity: 0.55; }
        }
      `}</style>

      {/*
        Portrait-native video — cover fills the full viewport with no bars.
        objectFit: cover + width/height 100% is all that is needed.
        No scaling hacks. No maxWidth/maxHeight constraints.
      */}
      <video
        ref={videoRef}
        src={VIDEO_URL}
        autoPlay
        muted
        playsInline
        onEnded={triggerEnd}
        onError={triggerEnd}
        style={{
          position:   'absolute',
          inset:       0,
          width:      '100%',
          height:     '100%',
          objectFit:  'cover',
          objectPosition: 'center center',
        }}
      />

      {/* Tap-to-skip hint — fades out before overlay does */}
      {!fading && (
        <p style={{
          position:      'absolute',
          bottom:        28,
          fontFamily:    "'Cinzel', serif",
          fontSize:      9,
          letterSpacing: '3.5px',
          textTransform: 'uppercase',
          color:         'rgba(212,168,83,0.42)',
          margin:        0,
          zIndex:        10,
          animation:     'mv-tap-hint 2.8s ease-in-out infinite',
          pointerEvents: 'none',
          userSelect:    'none',
        }}>
          Tap to skip
        </p>
      )}
    </div>
  );
}
